/**
 * FeatureAccessContext
 * 
 * Provides cached feature access information across the application.
 * Components can check if the current user has access to specific features.
 */

import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { authenticatedFetch } from '../utils/authApi';
import { useAuth } from '../components/Auth/AuthProvider';

interface FeatureAccess {
  feature_id: string;
  feature_type: string;
  display_name: string;
  has_access: boolean;
}

interface FeatureAccessContextType {
  // Feature access data
  accessibleFeatures: FeatureAccess[];
  loading: boolean;
  error: string | null;
  
  // Cache info
  cacheVersion: number;
  lastRefresh: Date | null;
  
  // Methods
  checkFeatureAccess: (featureId: string) => boolean;
  checkModelAccess: (modelId: string) => boolean;
  refreshAccess: () => Promise<void>;
  isFeatureLoaded: boolean;
}

const FeatureAccessContext = createContext<FeatureAccessContextType | undefined>(undefined);

interface FeatureAccessProviderProps {
  children: ReactNode;
}

export const FeatureAccessProvider: React.FC<FeatureAccessProviderProps> = ({ children }) => {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const [accessibleFeatures, setAccessibleFeatures] = useState<FeatureAccess[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cacheVersion, setCacheVersion] = useState(0);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);
  const [isFeatureLoaded, setIsFeatureLoaded] = useState(false);

  const refreshAccess = useCallback(async () => {
    // Don't fetch if not authenticated
    if (!isAuthenticated) {
      setLoading(false);
      setIsFeatureLoaded(true);  // Fail open - allow all features for anonymous
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      const response = await authenticatedFetch('/api/feature-access');
      const data = await response.json();
      
      if (data.success) {
        setAccessibleFeatures(data.features || []);
        setCacheVersion(prev => prev + 1);
        setLastRefresh(new Date());
        setIsFeatureLoaded(true);
      } else {
        // If API fails, default to allowing access (fail open for better UX)
        console.warn('Feature access API failed, defaulting to allow all');
        setError(data.error || 'Failed to load feature access');
        setIsFeatureLoaded(true);
      }
    } catch (err) {
      console.error('Failed to load feature access:', err);
      setError('Failed to load feature access');
      // Don't block the UI - fail open
      setIsFeatureLoaded(true);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  // Load when auth completes and user is authenticated
  useEffect(() => {
    if (!authLoading) {
      refreshAccess();
    }
  }, [authLoading, refreshAccess]);

  // Check if user has access to a specific feature
  const checkFeatureAccess = useCallback((featureId: string): boolean => {
    // If features haven't loaded yet or there was an error, default to allow
    if (!isFeatureLoaded || error) {
      return true;
    }
    
    // Check if feature is in accessible list
    const feature = accessibleFeatures.find(f => f.feature_id === featureId);
    
    // If no permission record exists for this feature, allow by default
    if (!feature) {
      return true;
    }
    
    return feature.has_access;
  }, [accessibleFeatures, isFeatureLoaded, error]);

  // Check model access (models have "model_" prefix)
  const checkModelAccess = useCallback((modelId: string): boolean => {
    return checkFeatureAccess(`model_${modelId}`);
  }, [checkFeatureAccess]);

  const value: FeatureAccessContextType = {
    accessibleFeatures,
    loading,
    error,
    cacheVersion,
    lastRefresh,
    checkFeatureAccess,
    checkModelAccess,
    refreshAccess,
    isFeatureLoaded
  };

  return (
    <FeatureAccessContext.Provider value={value}>
      {children}
    </FeatureAccessContext.Provider>
  );
};

// Hook for accessing feature permissions
export const useFeatureAccess = (): FeatureAccessContextType => {
  const context = useContext(FeatureAccessContext);
  if (context === undefined) {
    throw new Error('useFeatureAccess must be used within a FeatureAccessProvider');
  }
  return context;
};

// Convenience hook for checking a single feature
export const useHasFeatureAccess = (featureId: string): boolean => {
  const { checkFeatureAccess, isFeatureLoaded } = useFeatureAccess();
  
  // While loading, return true (fail open)
  if (!isFeatureLoaded) {
    return true;
  }
  
  return checkFeatureAccess(featureId);
};

// Convenience hook for checking model access
export const useHasModelAccess = (modelId: string): boolean => {
  const { checkModelAccess, isFeatureLoaded } = useFeatureAccess();
  
  if (!isFeatureLoaded) {
    return true;
  }
  
  return checkModelAccess(modelId);
};

export default FeatureAccessContext;
